import { resolvedTicketByID, updateTicket } from '@/modules/Ticket/services'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import { yupResolver } from '@hookform/resolvers/yup'
import {
    DEFAULT_AVOID,
    DEFAULT_INTERNAL_AVOID,
    DEFAULT_INTERNAL_REMEDIAL,
    DEFAULT_INTERNAL_ROOT_CAUSE,
    DEFAULT_REMEDIAL,
    DEFAULT_ROOT_CAUSE,
    resolvedIncidentSchema,
} from './schema'
import { CheckCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import HelpIcons from '@/icons/HelpIcons'
import { useAppStore } from '@/stores/useAppStore'
import contents from '@/modules/Ticket/help/ticket-resolved-help.md'
import TicketInternal from '@/modules/Ticket/components/Forms/TicketInternal'
import TicketRootCause from '@/modules/Ticket/components/Forms/TicketRootCause'
import TicketCAPA from '@/modules/Ticket/components/Forms/TicketCAPA'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import TicketTimeRelease from '@/modules/Ticket/components/Forms/TicketTimeRelease'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getServiceDowntimeLogs,
    endDowntime,
} from '@/modules/Service/services/sla'
import { SLADowntimeLog } from '@/modules/Service/types/sla'
import EndDowntimeModal from '@/modules/Service/components/SLA/Downtime/EndDowntimeModal'

interface TicketResolvedIncidentProps {
    ticket: any
}

const ResolvedIncidentTicket: React.FC<TicketResolvedIncidentProps> = ({
    ticket,
}) => {
    const { t } = useTranslation()
    const { setIsShowHelp, setHelpContent, isShowHelp } = useAppStore()
    const queryClient = useQueryClient()
    const { refetchAll: refetchLogs } = useDataGridRefetch({
        reactQueryKey: `ticket-logs-${ticket?.ticket_id}`,
    })

    // State for EndDowntimeModal
    const [showEndDowntimeModal, setShowEndDowntimeModal] = useState(false)
    const [selectedDowntime, setSelectedDowntime] =
        useState<SLADowntimeLog | null>(null)
    const [isEndingDowntime, setIsEndingDowntime] = useState(false)
    const [pendingResolve, setPendingResolve] = useState(false)

    // Fetch open downtimes linked to this ticket
    const { data: downtimeData, refetch: refetchDowntimes } =
        useQueryWithHandler({
            queryKey: [
                QUERY_KEYS.SLA_DOWNTIME,
                ticket?.service_id,
                'ticket',
                ticket?.ticket_id,
            ],
            queryFn: () =>
                getServiceDowntimeLogs(ticket?.service_id, {
                    page: 1,
                    page_size: 20,
                    q: '',
                }),
            enabled: !!ticket?.service_id && ticket.service_id > 0,
        })

    // Filter open downtimes linked to this ticket
    const openDowntimes = (downtimeData?.results || []).filter(
        (log: SLADowntimeLog) =>
            log.ticket_id === ticket?.ticket_id && !log.downtime_end
    )
    const hasOpenDowntimes = openDowntimes.length > 0

    const { control, handleSubmit, reset } = useForm({
        defaultValues: {
            ticket_remedial_measures: {
                text: ticket.ticket_remedial_measures || DEFAULT_REMEDIAL || '',
                attachments: [],
            },
            ticket_avoid: {
                text: ticket.ticket_avoid || DEFAULT_AVOID || '',
                attachments: [],
            },
            ticket_root_cause: {
                text: ticket.ticket_root_cause || DEFAULT_ROOT_CAUSE || '',
                attachments: [],
            },
            ticket_internal_root_cause: {
                text:
                    ticket.ticket_internal_root_cause ||
                    DEFAULT_INTERNAL_ROOT_CAUSE ||
                    '',
                attachments: [],
            },
            ticket_internal_avoid: {
                text:
                    ticket.ticket_internal_avoid ||
                    DEFAULT_INTERNAL_AVOID ||
                    '',
                attachments: [],
            },
            ticket_internal_remedial_measures: {
                text:
                    ticket.ticket_internal_remedial_measures ||
                    DEFAULT_INTERNAL_REMEDIAL ||
                    '',
                attachments: [],
            },
        },
        // @ts-ignore
        resolver: yupResolver(resolvedIncidentSchema(t)),
    })

    useEffect(() => {
        if (ticket) {
            reset({
                ticket_remedial_measures: {
                    text:
                        ticket.ticket_remedial_measures ||
                        DEFAULT_REMEDIAL ||
                        '',
                    attachments: [],
                },
                ticket_avoid: {
                    text: ticket.ticket_avoid || DEFAULT_AVOID || '',
                    attachments: [],
                },
                ticket_root_cause: {
                    text: ticket.ticket_root_cause || DEFAULT_ROOT_CAUSE || '',
                    attachments: [],
                },
                ticket_internal_root_cause: {
                    text:
                        ticket.ticket_internal_root_cause ||
                        DEFAULT_INTERNAL_ROOT_CAUSE ||
                        '',
                    attachments: [],
                },
                ticket_internal_avoid: {
                    text:
                        ticket.ticket_internal_avoid ||
                        DEFAULT_INTERNAL_AVOID ||
                        '',
                    attachments: [],
                },
                ticket_internal_remedial_measures: {
                    text:
                        ticket.ticket_internal_remedial_measures ||
                        DEFAULT_INTERNAL_REMEDIAL ||
                        '',
                    attachments: [],
                },
                ticket_time_release: ticket.ticket_time_release || dayjs(),
            })
        }
    }, [ticket, reset])

    const { mutateAsync: updateTicketMutation } = useMutationWithHandler(
        { mutationFn: updateTicket },
        {
            reactQueryKeys: QUERY_KEYS.TICKETS,
            showSuccessToast: false,
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [
                        QUERY_KEYS.TICKET_DETAIL,
                        String(ticket.ticket_id),
                    ],
                })
                refetchLogs()
            },
        }
    )

    const { mutateAsync: resolvedTicketMutation, isPending } =
        useMutationWithHandler(
            { mutationFn: resolvedTicketByID },
            {
                reactQueryKeys: QUERY_KEYS.TICKETS,
                successMessage: t('updated-success'),
                onSuccess: () => {
                    queryClient.invalidateQueries({
                        queryKey: [
                            QUERY_KEYS.TICKET_DETAIL,
                            String(ticket.ticket_id),
                        ],
                    })
                    refetchLogs()
                },
            }
        )

    const onSubmit = async (values: any) => {
        await updateTicketMutation({
            payload: {
                note: {
                    ...values,
                    ticket_id: ticket.ticket_id,
                    ticket_status: ticket?.ticket_status,
                    ticket_priority: ticket?.ticket_priority,
                    ticket_remedial_measures:
                        values?.ticket_remedial_measures?.text || '',
                    ticket_avoid: values?.ticket_avoid?.text || '',
                    ticket_root_cause: values?.ticket_root_cause?.text || '',
                    ticket_internal_root_cause:
                        values?.ticket_internal_root_cause?.text || '',
                    ticket_internal_remedial_measures:
                        values?.ticket_internal_remedial_measures?.text || '',
                    ticket_internal_avoid:
                        values?.ticket_internal_avoid?.text || '',
                    ticket_time_release:
                        values.ticket_time_release ||
                        dayjs(values.ticket_time_release).format(
                            'YYYY-MM-DD HH:mm:ss'
                        ),
                },
            },
        })

        // Check for open downtimes after update
        if (hasOpenDowntimes && openDowntimes.length > 0) {
            // Show modal to end first open downtime
            setSelectedDowntime(openDowntimes[0])
            setShowEndDowntimeModal(true)
            setPendingResolve(true)
        } else {
            // No open downtimes, resolve directly
            await resolvedTicketMutation({ ticket_id: ticket.ticket_id })
        }
    }

    // Handle ending downtime
    const handleEndDowntime = async (endTime: string) => {
        if (!selectedDowntime || !ticket?.service_id) return

        setIsEndingDowntime(true)
        try {
            await endDowntime(
                ticket.service_id,
                selectedDowntime.downtime_log_id,
                { downtime_end: endTime }
            )
            await refetchDowntimes()
            setShowEndDowntimeModal(false)
            setSelectedDowntime(null)

            // If pending resolve, call resolved API after closing downtime
            if (pendingResolve) {
                setPendingResolve(false)
                await resolvedTicketMutation({ ticket_id: ticket.ticket_id })
            }
        } catch (error) {
            console.error('Failed to end downtime:', error)
        } finally {
            setIsEndingDowntime(false)
        }
    }

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
                {/* Internal Report Fields */}
                <TicketInternal control={control} />

                {/* External Report Fields */}
                <div className="space-y-3 mt-3">
                    <TicketRootCause control={control} />
                    <TicketCAPA
                        control={control}
                        showAllFields={
                            ticket?.ticket_type_group === 'error_service'
                        }
                    />
                </div>

                {/* Time Release */}
                <div className="mt-3">
                    <TicketTimeRelease control={control} />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4">
                    <Button
                        type="submit"
                        variant="contained"
                        size="small"
                        startIcon={<CheckCircle className="w-4 h-4" />}
                        endIcon={
                            <HelpIcons
                                onMouseDown={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setIsShowHelp(!isShowHelp)
                                    setHelpContent(contents)
                                }}
                            />
                        }
                        loading={isPending}
                        disabled={isPending}
                        onClick={handleSubmit(onSubmit)}
                    >
                        {t('ticket-mark-resolved')}
                    </Button>
                </div>
            </form>

            {/* End Downtime Modal */}
            <EndDowntimeModal
                isOpen={showEndDowntimeModal}
                onClose={() => {
                    setShowEndDowntimeModal(false)
                    setSelectedDowntime(null)
                }}
                onConfirm={handleEndDowntime}
                downtime={selectedDowntime}
                isLoading={isEndingDowntime}
            />
        </>
    )
}

export default ResolvedIncidentTicket
